import { useCallback, useEffect, useState } from 'react';
import { getPushPublicKey, subscribePush, unsubscribePush } from '../services/kernelApiCommon';

function isSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// La chiave pubblica VAPID arriva dal server in base64url (RFC 4648 §5): va
// convertita in Uint8Array per pushManager.subscribe(), che non accetta
// stringhe.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Notifiche push reali (arrivano anche ad app chiusa, a differenza del
 * campanello in-app del kernel): richiedono il login — la sottoscrizione è
 * legata all'account — e il permesso del browser. Ogni dispositivo si
 * sottoscrive per conto suo: nessuna sincronizzazione tra dispositivi, lo
 * stato riflette solo quello corrente.
 */
export function usePushNotifications(isAuthenticated) {
  const [supported] = useState(isSupported);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!supported || !isAuthenticated) { setSubscribed(false); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, [supported, isAuthenticated]);

  const subscribe = useCallback(async () => {
    if (!supported || !isAuthenticated) return;
    setLoading(true);
    setError('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Permesso negato: puoi riattivarlo dalle impostazioni del sito nel browser.');
        return;
      }
      const publicKey = await getPushPublicKey();
      if (!publicKey) {
        setError('Le notifiche push non sono ancora configurate su questo server.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await subscribePush(sub.toJSON());
      setSubscribed(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Non è stato possibile attivare le notifiche su questo dispositivo.');
    } finally {
      setLoading(false);
    }
  }, [supported, isAuthenticated]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  return { supported, subscribed, loading, error, subscribe, unsubscribe };
}
